// Строка очереди приёмки: поданная заявка с чеком.
//
// Слева уменьшенное изображение чека либо честная плашка «без чека» — это разные факты, и
// пустой квадрат вместо второго читался бы как «изображение не загрузилось».
//
// Две кнопки быстрого действия живут ПРЯМО В СТРОКЕ, а не за открытием панели: цель по
// нажатиям — двадцать заявок за десять нажатий, и открытие панели на каждую её убивает.
//
// ## Проблемная заявка решается тоже отсюда (15.09.2026)
//
// До этого дня в строке было ровно одно решение — «Принять». Отклонить с причиной и вернуть
// на доработку можно было только открыв панель: нажатие на «Разобрать», нажатие в поле
// причины, нажатие на кнопку решения, а потом ещё закрыть панель и заново найти себя в
// очереди. Норма «двадцать заявок за десять нажатий» держится на том, что обычная заявка
// стоит пол-нажатия; три проблемных по четыре нажатия съедали её целиком.
//
// Решение — меню «Ещё» РЯДОМ с «Принять», а не вместо неё: на «Принять» норма и держится, и
// прятать самое частое действие под меню значило бы убить её другим способом. Меню взято
// готовым (`modules/settings/МенюДействий`), своего здесь не заводится: у продукта одно меню
// редких действий, с одним порядком, одной чертой перед опасным и одним красным цветом.
//
// ## Почему причина спрашивается прямо в строке, а не окном и не панелью
//
// Отклонение и возврат без причины сервер не принимает, и это верно: человек в поле обязан
// понять, что исправить. Значит где-то надо напечатать фразу. Окно перекрыло бы очередь и
// потребовало бы его закрыть; панель уводит из списка — ровно то, от чего уходим. Поле
// раскрывается в самой строке, фокус встаёт в него сам: иначе нажатие в поле было бы ещё
// одним нажатием, и весь выигрыш свёлся бы к нулю.

import { useEffect, useState } from 'react';

import { ПРИЗНАК_СТРОКИ } from '@/layout/клавиатура-списка';
import { МенюДействий } from '@/modules/settings/МенюДействий';
import type { ClaimOut } from '@/shared/api/endpoints/accountable';
import { formatKopecks } from '@/shared/money/format';
import { formatDateOnly } from '@/shared/time/format';
import * as Badge from '@/shared/ui/badge';
import * as Button from '@/shared/ui/button';
import * as Checkbox from '@/shared/ui/checkbox';
import { TextField } from '@/shared/ui/text-field';
import { cn } from '@/utils/cn';

import { CLAIM_STATUS_TITLES, CLAIM_STATUS_TONE } from '../dictionaries';

import { ReceiptThumb } from './ReceiptThumb';

/** Решение, которому нужна причина. Приёмке она не нужна и не спрашивается. */
export type РешениеСПричиной = 'reject' | 'return';

/** Слова каждого решения в одном месте: заголовок поля, подсказка, кнопка, занятость. */
interface СловаРешения {
  поле: string;
  подсказка: string;
  пример: string;
  кнопка: string;
  идёт: string;
  /** Красным — только необратимое. Возврат на доработку человек переживёт и пришлёт заново. */
  тон: 'error' | 'primary';
}

const РЕШЕНИЯ: Readonly<Record<РешениеСПричиной, СловаРешения>> = {
  reject: {
    поле: 'Причина отклонения',
    подсказка: 'Человек увидит её в приложении. Сумма останется висеть на нём.',
    пример: 'расход не по работе',
    кнопка: 'Отклонить',
    идёт: 'Отклоняем…',
    тон: 'error',
  },
  return: {
    поле: 'Что исправить',
    подсказка: 'Человек увидит это в приложении и пришлёт заявку заново.',
    пример: 'чек нечитаемый, пришлите заново',
    кнопка: 'Вернуть',
    идёт: 'Возвращаем…',
    тон: 'primary',
  },
};

export interface ClaimRowProps {
  claim: ClaimOut;
  checked: boolean;
  /** Идёт ли действие ИМЕННО по этой строке. Гвард по ключу, а не общий флаг на экран. */
  busy: boolean;
  /** Нет права принимать — кнопок нет, вместо них строка состояния. */
  canDecide: boolean;
  /**
   * Право отклонять и возвращать (`core.accountable.claim_reject`).
   *
   * Отдельно от `canDecide`: на сервере это разные права, и раздача их разойтись может.
   * Меню, за которым ждёт отказ, — это тот же отказ, только на два нажатия позже.
   */
  canReject: boolean;
  onToggle: (checked: boolean) => void;
  onAccept: () => void;
  onDecide: (decision: РешениеСПричиной, reason: string) => void;
  onOpen: () => void;
}

export function ClaimRow({
  claim,
  checked,
  busy,
  canDecide,
  canReject,
  onToggle,
  onAccept,
  onDecide,
  onOpen,
}: ClaimRowProps) {
  const сторнирована = claim.status === 'reversed';
  const [решение, setРешение] = useState<РешениеСПричиной | null>(null);
  const [причина, setПричина] = useState('');
  const [ошибка, setОшибка] = useState<string | undefined>(undefined);
  const полеПричины = `claim-reason-${claim.id}`;

  // Фокус в поле ставится сам, потому что нажатие в него было бы ЧЕТВЁРТЫМ нажатием на
  // заявку — а всё это затевалось ради того, чтобы их стало меньше. Ищем по опознавателю, а
  // не ссылкой: поле рисует общий `TextField`, и ссылки наружу он не отдаёт.
  useEffect(() => {
    if (решение === null) {
      return;
    }
    document.getElementById(полеПричины)?.focus();
  }, [решение, полеПричины]);

  function начать(вид: РешениеСПричиной) {
    setРешение(вид);
    setПричина('');
    setОшибка(undefined);
  }

  function подтвердить() {
    if (решение === null) {
      return;
    }
    if (причина.trim() === '') {
      // Те же слова, что в панели заявки: одно и то же требование, рассказанное двумя
      // способами, человек читает как два разных правила.
      setОшибка('Причина обязательна: человеку в поле надо понять, что исправить.');
      return;
    }
    setОшибка(undefined);
    onDecide(решение, причина.trim());
  }

  return (
    <div className='flex flex-col gap-2 border-b border-stroke-soft-200 px-4 py-3'>
      <div className='flex items-start gap-3'>
        {/* Галочка из дизайн-системы, а не своя: своя была на четыре точки меньше и с другим
            попаданием мышью, чем все остальные галочки продукта. */}
        <Checkbox.Root
          checked={checked}
          onCheckedChange={(значение) => onToggle(значение === true)}
          aria-label={`Отметить заявку «${claim.title}»`}
          className='mt-1 shrink-0'
        />

        <ReceiptThumb
          url={claim.receipts?.[0]?.thumb_url ?? null}
          alt={`Чек по заявке «${claim.title}»`}
        />

        <button
          // Строка для хода стрелками. У этой строки, в отличие от прочих, есть свои кнопки
          // решения — принять и отклонить, — поэтому признак стоит на кнопке открытия, а не
          // на всей карточке: стрелка обязана вести по людям, а не по кнопкам внутри строки.
          {...{ [ПРИЗНАК_СТРОКИ]: '' }}
          type='button'
          onClick={onOpen}
          className='flex min-w-0 flex-1 flex-col items-start gap-0.5 text-left'
        >
          <span
            className={cn(
              'text-label-sm tabular-nums text-text-strong-950',
              сторнирована && 'line-through',
            )}
          >
            {formatKopecks(claim.amount)}
          </span>
          {/* Наименование стоит ПЕРВЫМ и занимает всю ширину, категория ушла строкой ниже.
              Пока они шли вместе («Хозяйственные нужды · Питьевая вода на пост»), категория
              съедала половину строки, а обрезалось наименование — то есть ровно то, ради чего
              финотдел на заявку и смотрит: за что деньги. Категория при этом повторяется в
              очереди у каждой второй заявки и сама по себе решения не меняет. */}
          <span className='w-full truncate text-paragraph-xs text-text-strong-950'>
            {claim.title}
          </span>
          {/* Порядок фактов = порядок важности, потому что строка узкая (368 точек) и хвост
              обрезается. Первым КТО потратил, сразу за ним КОМУ передал — это второй уровень
              подотчёта, ради которого систему и купили, и обрезаться он не имеет права.
              Категория и дата идут последними: категория уже названа наименованием выше и
              решения не меняет, а дата в очереди почти всегда сегодняшняя.
              Слово «получатель» убрано: оно занимало десять знаков из тех, что не помещались,
              а второе имя в строке и так читается как получатель. */}
          <span className='w-full truncate text-paragraph-xs text-text-sub-600'>
            {claim.person_name}
            {claim.recipient_name !== null && <> → {claim.recipient_name}</>}
            {' · '}
            {claim.category_title}
            {claim.spent_at !== null && <> · {formatDateOnly(claim.spent_at)}</>}
          </span>
        </button>

        {/* Метка показывается, только когда она ЧТО-ТО говорит. Очередь отбирает заявки в
            одном состоянии — поданные, — и метка «Подана» стояла у всех строк подряд: у всех
            одинаковая метка не несёт ничего, а место в узкой строке занимает. Состояние
            «подана» и так видно по кнопкам решения ниже.
            Размер `medium`, а не `small`: у мелкой метки дизайн-системы буквы прописные, и
            это было единственное место во всём продукте, где текст кричал. */}
        {claim.status !== 'submitted' && (
          <Badge.Root variant='lighter' color={CLAIM_STATUS_TONE[claim.status]} size='medium'>
            {CLAIM_STATUS_TITLES[claim.status]}
          </Badge.Root>
        )}
      </div>

      {/* Кнопки своей строкой: на ширине колонки в 384 точки они наезжали на наименование
          заявки, и прочитать, за что расход, было нельзя. Поймано живым прогоном.

          ⚠️ «Принять» здесь ОБВОДКОЙ, а не заливкой, и это проверено попыткой.
          04.09.2026 её сделали заливкой — «главное действие экрана не выделено», — и сторож
          «главная кнопка на экране одна» покраснел. Он прав: главное действие очереди стоит
          НАД списком и звучит «Принять 4 на 47 850», а построчная кнопка ему не конкурент, она
          сокращение для одной заявки. Четыре залитых кнопки столбиком спорят с ней и между
          собой. Различие с «Разобрать» несёт цвет: у «Принять» он основной, у второй серый. */}
      {claim.status === 'submitted' && (canDecide || canReject) && (
        <div className='flex flex-wrap items-center justify-end gap-2'>
          {canDecide && (
            <>
              <Button.Root
                variant='primary'
                mode='stroke'
                size='xxsmall'
                onClick={onAccept}
                disabled={busy}
              >
                {busy && решение === null ? 'Принимаем…' : 'Принять'}
              </Button.Root>
              <Button.Root variant='neutral' mode='stroke' size='xxsmall' onClick={onOpen}>
                Разобрать
              </Button.Root>
            </>
          )}
          {/* Меню редких решений. Общее с разделом «Настройки» намеренно: порядок пунктов,
              черта перед опасным и красный цвет должны быть одни на весь продукт — рука
              запоминает место, а не читает заново. Подпись меню — наименование заявки: меню
              открыто одно, но строк двадцать, и «по какой заявке я решаю» экран обязан
              отвечать сам. */}
          {canReject && (
            <МенюДействий
              подпись={claim.title}
              пункты={[
                {
                  подпись: 'Вернуть на доработку',
                  выполнить: () => начать('return'),
                },
                {
                  // Опасное: отклонение окончательно. Заявки не правят — отклонённую подают
                  // заново, а сумма остаётся висеть на человеке.
                  подпись: 'Отклонить с причиной',
                  опасное: true,
                  выполнить: () => начать('reject'),
                },
              ]}
            />
          )}
        </div>
      )}
      {claim.status === 'submitted' && !canDecide && !canReject && (
        <span className='text-right text-paragraph-xs text-text-sub-600'>
          на проверке у финотдела
        </span>
      )}

      {/* Причина решения. Раскрывается ПОД строкой, а не окном поверх очереди: следующая
          заявка остаётся на виду, и закрывать после решения нечего. */}
      {решение !== null && (
        <div className='flex flex-col gap-2 rounded-lg bg-bg-weak-50 p-3'>
          <TextField
            id={полеПричины}
            label={РЕШЕНИЯ[решение].поле}
            required
            value={причина}
            onChange={setПричина}
            placeholder={РЕШЕНИЯ[решение].пример}
            error={ошибка}
            hint={РЕШЕНИЯ[решение].подсказка}
          />
          <div className='flex justify-end gap-2'>
            <Button.Root
              variant='neutral'
              mode='ghost'
              size='xxsmall'
              type='button'
              onClick={() => setРешение(null)}
            >
              Отмена
            </Button.Root>
            <Button.Root
              variant={РЕШЕНИЯ[решение].тон}
              mode='stroke'
              size='xxsmall'
              type='button'
              onClick={подтвердить}
              disabled={busy}
            >
              {busy ? РЕШЕНИЯ[решение].идёт : РЕШЕНИЯ[решение].кнопка}
            </Button.Root>
          </div>
        </div>
      )}
    </div>
  );
}
